/* eslint-disable prettier/prettier */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMainProvider } from '../../context/MainProvider';
import GenericTablePage from '../GenericTablePage/GenericTablePage';
import {
  GenericColumnTypes,
  GenericTableColumn,
  GenericTableRow,
} from '../../interfaces/GenericTable';
import EmptyClosure from '../../utils/closures';
import RadiographyForm from './RadiographyForm';
import { CRUDModes } from '../../interfaces/CRUD';
import { UserTypes } from '../../interfaces/User';
import { gettWithToken, previewUserID } from '../../utils/server';
import { Radiographies, SimplifiedRadiographies } from '../../interfaces/Radiographies';
import { getPatientFullName } from '../../utils/fns';
import { Patient } from '../../interfaces/Patients';
import { AlertTypes } from '../../interfaces/MainContextInitialValues';
import {
  IOSHandlerFunctions,
  isIOSApp,
  isAndroid,
  postWebkitMessage,
} from '../../interfaces/WebkitWindow';
import ImagePreview from '../ImagePreview/ImagePreview';
import ImageViewerPopup from '../../components/ImageViewerPopup/ImageViewerPopup';

const RadiographiesPage = (): JSX.Element => {
  const {
    setLoading,
    user,
    setAlertType,
    setAlertMessage,
    setOpenAlert,
    currentPatient,
    setLoginPopupOpen,
    handleLogout,
  } = useMainProvider();
  const [entries, setEntries] = useState<Radiographies[]>([]);
  const [rows, setRows] = useState<GenericTableRow[]>([]);
  const [columns, setColumns] = useState<GenericTableColumn[]>([]);
  const [open, setOpen] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [imageURL, setImageURL] = useState('');
  const [selectedRow, setSelectedRow] = useState<number>(-1);
  const [mode, setMode] = useState<CRUDModes>(CRUDModes.Create);
  const [selectedFile, setSelectedFile] = useState('');

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const fetchResult = await gettWithToken<Radiographies[]>(
        `/patient/get-radiographies?patientId=${
          currentPatient ? currentPatient.id : undefined
        }`,
        handleLogout,
      );
      if (fetchResult.success) {
        const orderedRows = fetchResult.data.sort((a, b) => {
          if (a.id && b.id) {
            return a.id > b.id ? -1 : 1;
          }
          return 1;
        });
        setEntries(orderedRows);
        setColumns([
          {
            name: 'id',
            displayName: 'ID',
            type: GenericColumnTypes.Numeric,
            sortable: true,
          },
          {
            name: 'fecha',
            displayName: 'Fecha de creación',
            type: GenericColumnTypes.Date,
            sortable: true,
          },
          {
            name: 'titulo',
            displayName: 'Titulo',
            type: GenericColumnTypes.Text,
            sortable: true,
          },
          {
            name: 'descripcion',
            displayName: 'Descripcion',
            type: GenericColumnTypes.Text,
            sortable: true,
          },
          {
            name: 'moreInformation',
            displayName: 'Consultar',
            type: GenericColumnTypes.Action,
            sortable: false,
            onActionButtonClick: (index) => {
              setSelectedRow(index);
              setMode(CRUDModes.Update);
              setOpen(true);
            },
          },
          {
            name: 'preview',
            displayName: 'Ver',
            type: GenericColumnTypes.Action,
            sortable: false,
            onActionButtonClick: (index) => {
              const currentInformation = fetchResult.data[index];
              if (currentInformation && currentInformation.url) {
                setSelectedFile(currentInformation.url);
              }
            },
          },
          {
            name: 'download',
            displayName: 'Descargar',
            type: GenericColumnTypes.Action,
            sortable: false,
            onActionButtonClick: (index) => {
              const currentInformation = fetchResult.data[index];
              if (currentInformation && currentInformation.url) {
                if (isIOSApp() && currentInformation.url) {
                  const splitURL = currentInformation.url.split('/');
                  const fileName = splitURL[splitURL.length - 1];
                  postWebkitMessage({
                    function: IOSHandlerFunctions.DownloadData,
                    download: {
                      url: currentInformation.url,
                      fileName,
                    },
                  });
                }

                const currentUrlString = currentInformation.url as string;

                if (!isAndroid()) {
                  // Change to https://stackoverflow.com/questions/17527713/force-browser-to-download-image-files-on-click when security implemented
                  const link = document.createElement('a');
                  link.href = currentUrlString;
                  link.setAttribute('target', '_blank');
                  link.setAttribute('download', currentInformation.url as string);
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }

                if (isAndroid() && currentUrlString.includes('.pdf')) {
                  // Change to https://stackoverflow.com/questions/17527713/force-browser-to-download-image-files-on-click when security implemented
                  const link = document.createElement('a');
                  link.href = currentUrlString;
                  link.setAttribute('target', '_blank');
                  link.setAttribute('download', currentInformation.url as string);
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }

                if (isAndroid() && !currentUrlString.includes('.pdf')) {
                  setImageURL(currentUrlString);
                  setOpenPreview(true);
                }
              }
            },
          },
        ]);
        const simplifiedData: SimplifiedRadiographies[] = orderedRows.map(
          (radiography) => {
            return {
              id: radiography.id,
              fecha: radiography.fecha,
              titulo: radiography.titulo,
              descripcion: radiography.descripcion,
              moreInformation: 'Consultar',
              preview: 'Ver',
              download: 'Descargar',
            };
          },
        );
        setRows(simplifiedData);
      }
      setLoading(false);
    } catch (e: any) {
      handleLogout(e);
      if (JSON.stringify(e.response.data)) {
        setLoading(false);
        setAlertType(AlertTypes.Error);
        setAlertMessage(
          `Error al obtener radiogradías : ${JSON.stringify(e.response.data)} `,
        );
        setOpenAlert(true);
      } else {
        setLoading(false);
        setAlertType(AlertTypes.Error);
        setAlertMessage(`Error en el servidor: ${e}`);
        setOpenAlert(true);
      }
    }
  };

  // Avoid infinite loop

  const fetchCallback = useCallback(fetchEntries, [
    currentPatient,
    setAlertMessage,
    setAlertType,
    setLoading,
    setOpenAlert,
  ]);

  useEffect(() => {
    fetchCallback().then(EmptyClosure).catch(EmptyClosure);
  }, [fetchCallback]);

  return (
    <>
      <RadiographyForm
        setOpen={setOpen}
        mode={mode}
        open={open}
        onSuccess={fetchCallback}
        values={selectedRow !== -1 ? entries[selectedRow] : undefined}
        closeFunction={() => {
          setSelectedRow(-1);
        }}
      />

      <ImagePreview open={openPreview} setOpen={setOpenPreview} imageURL={imageURL} />
      <ImageViewerPopup
        link={selectedFile}
        open={!!selectedFile}
        onClose={() => setSelectedFile('')}
      />
      <GenericTablePage
        title={`Estudios de gabinete${
          currentPatient
            ? ` de ${getPatientFullName(currentPatient.initialData as Patient)}`
            : ''
        }`}
        rows={rows}
        columns={columns}
        canDelete={false}
        canEdit
        canCreate={
          (user && currentPatient ? user.typeId === UserTypes.DoctorType : false) ||
          (user && user.typeId === UserTypes.PatientType)
        }
        onCreate={() => {
          if (user && user.id === previewUserID) {
            setLoginPopupOpen(true);
          } else {
            setOpen(true);
            setSelectedRow(-1);
            setMode(CRUDModes.Create);
          }
        }}
      />
    </>
  );
};

export default RadiographiesPage;
